import {gsap} from "gsap";
import {ScrollTrigger} from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

class ChartGraph {

  static instance = null;

  static create(options) {
    if(!ChartGraph.instance) {
      ChartGraph.instance = new ChartGraph(options);
    }
    return ChartGraph.instance;
  }

  static destroy() {
    if(ChartGraph.instance) {
      ChartGraph.instance.beforeDestroy();
      ChartGraph.instance = null;
    }
  }

  constructor() {
    this.charts = document.querySelectorAll('[data-chart-graph]');
    if(!this.charts.length) return;

    this.init();
  }

  init() {
    this.charts.forEach((chart) => {
      this.initChart(chart);
    })
  }

  initChart(chart) {
    const chartType = chart.dataset.chartGraph;
    switch(chartType) {
      case 'line':
        this.initLineChart(chart);
        break;
      case 'bar':
        this.initBarChart(chart);
        break;
      case 'bar-double':
        this.initBarDoubleChart(chart);
        break;
      case 'counters':
        this.initCountersChart(chart);
        break;
      default:
        console.error('Chart type not found');
    }
  }

  initLineChart(chart) {
    const lines = chart.querySelectorAll('.w-line');

    if(!lines.length) return;

    lines.forEach((polyline) => {
      const length = polyline.getTotalLength();
      polyline.style.strokeDasharray = length;
      polyline.style.strokeDashoffset = length;
      polyline.dataset.length = length;
    })

    let tl = gsap.timeline({
      scrollTrigger: {
        trigger: chart,
        start: "top 90%",
        end: "bottom 10%",
        scroller: "[data-scroll-container]",
        once: true,

      }, onComplete: (a) => {
        if(tl.scrollTrigger) tl.scrollTrigger.kill();
        if(tl) tl.kill();
        tl = null;
      },
    }).to(lines, {
      strokeDashoffset: 0,
      duration: 2,
      ease: 'power1.out',
      stagger: 0.15
    })
  }

  initBarChart(chart) {
    const digits = chart.dataset.digits || 0;
    const digitsMultiplier = Math.pow(10, digits);
    const bars = chart.querySelectorAll('.w-bar');
    if(!bars.length) return;

    bars.forEach((bar) => {
      bar.style.height = 0;

      const value = bar.dataset.value;
      if(value) {
        bar.innerhtml = `<span>0%</span`;
      }
    })

    let tl = gsap.timeline({
      scrollTrigger: {
        trigger: chart,
        start: "top 90%",
        end: "bottom 10%",
        scroller: "[data-scroll-container]",
        once: true,

      }, onComplete: (a) => {
        if(tl.scrollTrigger) tl.scrollTrigger.kill();
        if(tl) tl.kill();
        tl = null;
      },
    }).to(bars, {
      height: (index, target) => {
        const value = target.dataset.bar;
        return `${value}%`;
      },
      onUpdate: () => {
        const progress = tl.progress();

        bars.forEach((bar) => {
          const finalValue = bar.dataset.value;
          if(finalValue) {
            const value = Math.round(finalValue * progress * digitsMultiplier) / digitsMultiplier;
            bar.innerHTML = `<span>${value}%</span>`;
          }
        })
      },
      duration: 2,
      ease: 'power1.out',
      stagger: 0.15
    })
  }

  initBarDoubleChart(chart) {
    const digits = chart.dataset.digits || 0;
    const digitsMultiplier = Math.pow(10, digits);
    const postfix = chart.dataset.postfix || '';
    const bars = chart.querySelectorAll('.w-bar');
    const bars0 = chart.querySelectorAll('.w-bar--0');
    const bars1 = chart.querySelectorAll('.w-bar--1');
    if(!bars.length) return;

    bars.forEach((bar) => {
      bar.style.height = 0;

      const value = bar.dataset.value;
      if(value) {
        bar.innerhtml = `<span>0${postfix}</span`;
      }
    })

    let tl = gsap.timeline({
      scrollTrigger: {
        trigger: chart,
        start: "top 90%",
        end: "bottom 10%",
        scroller: "[data-scroll-container]",
        once: true,

      }, onComplete: (a) => {
        if(tl.scrollTrigger) tl.scrollTrigger.kill();
        if(tl) tl.kill();
        tl = null;
      },
    })
      .to(bars0, {
        height: (index, target) => {
          const value = target.dataset.bar;
          return `${value}%`;
        },
        onUpdate: () => {
          const progress = tl.progress();

          bars0.forEach((bar) => {
            const finalValue = bar.dataset.value;
            if(finalValue) {
              const value = Math.round(finalValue * progress * digitsMultiplier) / digitsMultiplier;
              bar.innerHTML = `<span>${value}${postfix}</span>`;
            }
          })
        },
        duration: 2,
        ease: 'power1.out',
        stagger: 0.15
      })
      .to(bars1, {
        height: (index, target) => {
          const value = target.dataset.bar;
          return `${value}%`;
        },
        onUpdate: () => {
          const progress = tl.progress();

          bars1.forEach((bar) => {
            const finalValue = bar.dataset.value;
            if(finalValue) {
              const value = Math.round(finalValue * progress * digitsMultiplier) / digitsMultiplier;
              bar.innerHTML = `<span>${value}${postfix}</span>`;
            }
          })
        },
        duration: 2,
        ease: 'power1.out',
        stagger: 0.15
      }, 0.7)
  }

  initCountersChart(chart) {
    const counters = chart.querySelectorAll('.w-counter');
    if(!counters.length) return;
    const digits = chart.dataset.digits || 0;
    const digitsMultiplier = Math.pow(10, digits);

    counters.forEach((counter) => {
      const value = counter.dataset.value;
      const postfix = counter.dataset.postfix || '';
      if(value) {
        counter.innerHTML = `<span>0${postfix}</span>`;
      }
    })

    let tl = gsap.timeline({
      scrollTrigger: {
        trigger: chart,
        start: "top 90%",
        end: "bottom 10%",
        scroller: "[data-scroll-container]",
        once: true,

      }, onComplete: (a) => {
        if(tl.scrollTrigger) tl.scrollTrigger.kill();
        if(tl) tl.kill();
        tl = null;
      },
    }).to(counters, {
      onUpdate: () => {
        const progress = tl.progress();

        counters.forEach((counter) => {
          const finalValue = counter.dataset.value;
          const postfix = counter.dataset.postfix || '';
          if(finalValue) {
            const value = Math.round(finalValue * progress * digitsMultiplier) / digitsMultiplier;
            counter.innerHTML = `<span>${value}${postfix}</span>`;
          }
        })
      },
      duration: 2,
      ease: 'power1.out',
      stagger: 0.25
    })
  }

  beforeDestroy() {
    this.charts = null;
  }
}

export default ChartGraph
